// Terrain art: per-chunk offscreen canvases (LRU, version-keyed), painterly
// deterministic hex tiles, coast foam, roads baked into the cache.
// Owner: client-render.

import { CHUNK, DIRS, SQRT3, chunkKey, hexToPixel as flatHexToPixel, pixelToHex as flatPixelToHex, tileFromIdx } from '../../shared/hex'
import { Terrain, TERRAIN } from '../../shared/terrain'
import { rngFor } from '../../shared/rng'
import { BUILDINGS, buildingFootprint, isBoxOrder, isTerraform } from '../../shared/constants'
import { levelTarget } from '../../shared/elevation'
import type { BuildingType } from '../../shared/types'
import { HEX_SIZE, type ClientChunk, type GameState } from '../state'
import { drawItemGlyph } from './entities'

const S = HEX_SIZE

// ---- view rotation (Q/E, animated 60° steps) -----------------------------------
// A hex grid has 6-fold symmetry, so k·60° rotation maps the tiling onto itself and
// stays clean. cam.rot is the (unbounded) target orientation; cam.rotF is the eased
// current index (float) that glides toward it — the smooth spin. Positions rotate
// around the origin here; the tilt-squash + pan run after, in the composite, so lift
// stays screen-up and sprites stay upright with no ctx.rotate. "SA" = screen-aligned.
//
// Two angles: the CONTINUOUS view angle (rotF, for entities/overlays/picking, and for
// placing terrain bitmaps at the in-between spin angle) and the DISCRETE bake angle
// (rot, the orientation each chunk bitmap is baked at — kept coarse so it stays cached
// through the spin). Terrain bitmaps bake at the discrete angle and are re-rotated to
// the continuous angle at composite time (renderer), so a spin needs no per-frame bake.
export const HEX_TAU6 = Math.PI / 3 // 60°
/** Continuous display angle (radians) — the eased spin. */
export function camAngle(st: GameState): number {
  return (st.cam.rotF ?? st.cam.rot ?? 0) * HEX_TAU6
}
/** Discrete target angle (radians) — the orientation terrain bitmaps bake at. */
export function bakeAngle(st: GameState): number {
  return (st.cam.rot ?? 0) * HEX_TAU6
}
/** Target orientation normalised to 0..5 — bitmap cache key + stored bake rot. */
export function bakeRotIdx(st: GameState): number {
  return ((((st.cam.rot ?? 0) % 6) + 6) % 6)
}
/** Rotate (x,y) around the origin by +angle (world → screen-aligned). */
export function rotXY(x: number, y: number, ang: number): { x: number; y: number } {
  if (!ang) return { x, y }
  const c = Math.cos(ang), s = Math.sin(ang)
  return { x: x * c - y * s, y: x * s + y * c }
}
/** World point → screen-aligned (rotated) point for the current (continuous) view. */
export function viewRotate(st: GameState, x: number, y: number): { x: number; y: number } {
  return rotXY(x, y, camAngle(st))
}
/** Screen-aligned point (or a screen delta) → unrotated world for the current view. */
export function viewUnrotate(st: GameState, x: number, y: number): { x: number; y: number } {
  return rotXY(x, y, -camAngle(st))
}

// Hot-path rotation for the wrapped hexToPixel/pixelToHex below. Set to the CONTINUOUS
// view angle for entity/overlay/pick math, and swapped to the DISCRETE bake angle
// (setBakeRot) only while a chunk bitmap is rendered so the bake stays cached.
let mCos = 1, mSin = 0, mAng = 0
function setModAngle(a: number): void {
  if (a === mAng) return
  mAng = a; mCos = Math.cos(a); mSin = Math.sin(a)
}
/** Point the wrapped hex↔pixel at the continuous view angle (entities/overlays/pick). */
export function setViewRot(st: GameState): void { setModAngle(camAngle(st)) }
/** Point them at the discrete bake angle while a chunk bitmap is being rendered. */
export function setBakeRot(st: GameState): void { setModAngle(bakeAngle(st)) }
/** Hex (q,r) → screen-aligned world position (rotated by the current module angle). */
export function hexToPixel(q: number, r: number, size: number): { x: number; y: number } {
  const p = flatHexToPixel(q, r, size)
  if (!mAng) return p
  return { x: p.x * mCos - p.y * mSin, y: p.x * mSin + p.y * mCos }
}
/** Screen-aligned world point → hex (q,r) (inverse of the current module angle). */
export function pixelToHex(wx: number, wy: number, size: number): { q: number; r: number } {
  if (mAng) { const x = wx * mCos + wy * mSin, y = -wx * mSin + wy * mCos; wx = x; wy = y }
  return flatPixelToHex(wx, wy, size)
}

// ---- small color utils -------------------------------------------------------

export type RGB = [number, number, number]

export function rgb(hex: string): RGB {
  const h = hex.replace('#', '')
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

export function css(c: RGB, a = 1): string {
  const r = Math.max(0, Math.min(255, Math.round(c[0])))
  const g = Math.max(0, Math.min(255, Math.round(c[1])))
  const b = Math.max(0, Math.min(255, Math.round(c[2])))
  return a >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${a})`
}

export function shade(c: RGB, f: number): RGB {
  return [c[0] * f, c[1] * f, c[2] * f]
}

export function mixRgb(a: RGB, b: RGB, t: number): RGB {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

// ---- state readers (used across render modules) --------------------------------

export function chunkAt(st: GameState, q: number, r: number): ClientChunk | undefined {
  return st.chunks.get(chunkKey(Math.floor(q / CHUNK), Math.floor(r / CHUNK)))
}

function lidx(q: number, r: number): number {
  const lq = q - Math.floor(q / CHUNK) * CHUNK
  const lr = r - Math.floor(r / CHUNK) * CHUNK
  return lr * CHUNK + lq
}

export function terrainAt(st: GameState, q: number, r: number): Terrain | undefined {
  const c = chunkAt(st, q, r)
  return c ? (c.tiles[lidx(q, r)] as Terrain) : undefined
}

/** Height tier of a hex, or undefined if the chunk isn't loaded or carries no
 *  elevation (flat world). Mirrors terrainAt for cross-chunk cliff drawing. */
export function elevAt(st: GameState, q: number, r: number): number | undefined {
  const c = chunkAt(st, q, r)
  return c && c.elev ? c.elev[lidx(q, r)] : undefined
}

/** True when a standing (choppable) tree grows on this grass hex. Woodland is a
 *  per-tile flag over ordinary grass, not a terrain type. */
export function treeAt(st: GameState, q: number, r: number): boolean {
  const c = chunkAt(st, q, r)
  return !!c?.trees && c.trees[lidx(q, r)] === 1
}

/** Which site-prep phase an unfinished building site is in, mirrored from the
 *  server's sitePrep off the same streamed data (trees layer + tiers): 'clear'
 *  while a tree still stands on the footprint (EVERY type but chop — roads, piers,
 *  ramps and terraform included), 'level' while any footprint hex is off the
 *  majority tier (levelTarget; structures only — road/pier/ramp/terraform skip the
 *  levelling half), null once the plot is ready — the moment the see-through
 *  planned-works overlay gives way to the solid blueprint. Only chop never preps:
 *  it IS the fell. */
export function sitePrepPhase(
  st: GameState,
  b: { type: BuildingType; q: number; r: number; dir?: number; done: boolean },
): 'clear' | 'level' | null {
  if (b.done) return null
  if (b.type === 'chop' || b.type === 'plant') return null // forestry never clears or levels its own plot
  const cells = buildingFootprint(b.type, b.q, b.r, b.dir)
  for (const c of cells) if (treeAt(st, c.q, c.r)) return 'clear'
  // Roads, piers, ramps and terraform orders get only the clearing half — a road
  // follows the ground, a pier's deck hangs over water, a ramp needs its cliff,
  // and a dig/fill IS the earthwork.
  if (b.type === 'road' || b.type === 'pier' || b.type === 'ramp' || isTerraform(b.type)) return null
  const target = levelTarget(cells, (hq, hr) => elevAt(st, hq, hr))
  if (target !== undefined) {
    for (const c of cells) {
      const t = elevAt(st, c.q, c.r)
      if (t !== undefined && t !== target) return 'level'
    }
  }
  return null
}

/** The single glyph to float over a construction SITE, telling the player what
 *  the plot needs RIGHT NOW: 🪓 fell the trees, 🪏 dig the high ground down,
 *  ⏫ fill the low ground up, 📦 haul materials in (the plot is ready but the
 *  stores can't feed the build), 🔨 raise the structure. null once done or for
 *  box orders (chop/dig/fill carry their own badge). Mirrors the server's work
 *  order so the icon always names the current bottleneck. */
export function siteStageIcon(
  st: GameState,
  b: {
    type: BuildingType; q: number; r: number; dir?: number; done: boolean
    buffer?: Partial<Record<string, number>>
  },
): string | null {
  if (b.done || isBoxOrder(b.type)) return null
  const cells = buildingFootprint(b.type, b.q, b.r, b.dir)
  // 1. standing trees on the plot come down first
  for (const c of cells) if (treeAt(st, c.q, c.r)) return '🪓'
  // 2. level the ground (structures only; roads/piers/ramps/terraform skip it)
  if (!(b.type === 'road' || b.type === 'pier' || b.type === 'ramp' || isTerraform(b.type))) {
    const target = levelTarget(cells, (hq, hr) => elevAt(st, hq, hr))
    if (target !== undefined) {
      let cut = false, fill = false
      for (const c of cells) {
        const t = elevAt(st, c.q, c.r)
        if (t === undefined) continue
        if (t > target) cut = true
        else if (t < target) fill = true
      }
      if (cut) return '🪏' // high ground dug down (cuts feed the fills)
      if (fill) return '⏫' // low ground raised
    }
  }
  // 3. plot ready: hauling materials in vs actively raising the structure. The
  //    build consumes cost/buildTime per tick — if the delivered buffer can't
  //    fund even that slice, the crew is stalled waiting on haulers.
  const def = BUILDINGS[b.type]
  for (const res in def.cost) {
    const need = (def.cost as Record<string, number>)[res] ?? 0
    if (need <= 0) continue
    const slice = need / def.buildTime
    if ((b.buffer?.[res] ?? 0) < slice - 1e-9) return '📦'
  }
  return '🔨'
}

/** Tier used for the 2.5D LIFT and cliff faces (not gameplay). Water always renders
 *  at sea level (tier 0) so the ocean is one flat plane — only land rises, and only
 *  land draws a cliff down to the waterline. undefined when the hex has no elevation
 *  data (flat world / unloaded chunk), matching elevAt so cliff code degrades the same. */
function renderTier(st: GameState, q: number, r: number): number | undefined {
  const c = chunkAt(st, q, r)
  if (!c || !c.elev) return undefined
  const i = lidx(q, r)
  return isWater(c.tiles[i] as Terrain) ? 0 : c.elev[i]
}

// ---- 2.5D oblique projection ---------------------------------------------------
/** Vertical foreshorten of the ground plane — the "camera angle". <1 tilts the
 *  map back so you look across it; heights (below) are pre-divided by it so they
 *  still read at full height on screen after the camera squashes y. */
export const GROUND_TILT = 0.62
/** On-screen px a tile rises per elevation tier (post-squash). */
export const LIFT_PER_TIER = 12

// Directional cliff-face lighting: a low sun in front-left. Per hex-edge
// direction (matching DIRS) — the front-facing walls we actually see (down-left
// d4, down-right d5) catch the most light so cliffs read as lit blocks, not
// dark cracks; the rarely-seen back walls stay dim. See DIRS pixel normals.
const CLIFF_LIGHT: number[] = (() => {
  const Lx = -0.34, Ly = 0.94 // light direction in screen space (down, slightly left)
  const norm: [number, number][] = [
    [1, 0], [0.5, -0.866], [-0.5, -0.866], [-1, 0], [-0.5, 0.866], [0.5, 0.866],
  ]
  return norm.map(([nx, ny]) => 0.6 + 0.28 * Math.max(0, nx * Lx + ny * Ly))
})()
/** World-space lift per tier: LIFT_PER_TIER / GROUND_TILT so the camera's y-squash
 *  brings it back to LIFT_PER_TIER on screen. Clamped so extreme tiers stay on the
 *  (bounded) chunk canvas. */
export function tierLiftPx(tier: number): number {
  return (Math.max(-8, Math.min(8, tier)) * LIFT_PER_TIER) / GROUND_TILT
}
/** World-px vertical lift for the hex at (q,r): 0 on flat worlds or when the 2.5D
 *  view is toggled off, so every draw/pick call degrades to the flat top-down. */
export function elevLift(st: GameState, q: number, r: number): number {
  if (!st.hasElev || !st.lift25) return 0
  const t = renderTier(st, q, r) // water rides the flat sea, not its own depth
  return t === undefined ? 0 : tierLiftPx(t)
}

/** World (flat-plane) point → the hex under it, accounting for the 2.5D lift. Sweeping
 *  the assumed height, pixelToHex(wx, wy + lift(t)) walks the real tiles along the
 *  cursor's screen ray (a raised tile's flat centre sits below where its top is drawn).
 *  A tile is a STRICT hit when the assumed tier equals its real tier — then the cursor
 *  genuinely sits on that tile's drawn top, so strict hits always win (frontmost first),
 *  which keeps a click near a tile's downhill edge on that tile instead of sliding to
 *  the lower one in front. Only when the cursor is on a cliff FACE (over no top at all)
 *  do we fall back to the nearest lifted top-centre — the low tile at the base, whose
 *  top is just below the cursor, rather than the tall hex behind. Screen-weighted so the
 *  y-squash doesn't bias the distance. Flat inverse when elevation is off. */
export function hexAtWorld(st: GameState, wx: number, wy: number): { q: number; r: number } {
  setViewRot(st) // (wx,wy) are screen-aligned; the wrapped hex↔pixel below de-rotate
  if (st.hasElev && st.lift25) {
    let strict: { q: number; r: number } | null = null
    let strictY = -Infinity // frontmost (largest ground-y) among strict hits
    let near: { q: number; r: number } | null = null
    let nearD = Infinity
    const seen = new Set<number>()
    for (let t = 8; t >= -8; t--) {
      const c = pixelToHex(wx, wy + tierLiftPx(t), S)
      const rt = renderTier(st, c.q, c.r) // its real tier (water → sea level 0)
      if (rt === undefined) continue
      const key = c.q * 100000 + c.r
      if (seen.has(key)) continue
      seen.add(key)
      const p = hexToPixel(c.q, c.r, S)
      if (rt === t && p.y > strictY) { strictY = p.y; strict = c } // cursor on this top
      const dx = p.x - wx
      const dy = (p.y - tierLiftPx(rt) - wy) * GROUND_TILT // lifted top-centre, screen-weighted
      const d = dx * dx + dy * dy
      if (d < nearD) { nearD = d; near = c }
    }
    if (strict) return strict
    if (near) return near
  }
  return pixelToHex(wx, wy, S)
}

export function roadAt(st: GameState, q: number, r: number): boolean {
  const c = chunkAt(st, q, r)
  return !!c && c.roads.has(lidx(q, r))
}

export function ownerAt(st: GameState, q: number, r: number): string | undefined {
  const c = chunkAt(st, q, r)
  return c ? c.owners.get(lidx(q, r)) : undefined
}

// ---- hex path helpers -----------------------------------------------------------

/** Pointy-top hexagon path around (x,y) with circumradius size. */
export function hexPathAt(ctx: CanvasRenderingContext2D, x: number, y: number, size: number): void {
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 90)
    const px = x + size * Math.cos(a)
    const py = y + size * Math.sin(a)
    if (i === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  }
  ctx.closePath()
}

/** Endpoints of the edge shared with neighbor in direction di, inset toward center by inset (0..0.5 of the center gap). */
export function hexEdge(
  x: number, y: number, di: number, size: number, inset = 0.5,
): { x1: number; y1: number; x2: number; y2: number; mx: number; my: number } {
  const d = DIRS[di]
  const v = hexToPixel(d.q, d.r, size)
  const mx = x + v.x * inset
  const my = y + v.y * inset
  const len = Math.hypot(v.x, v.y)
  const px = -v.y / len
  const py = v.x / len
  const h = size * 0.5
  return { x1: mx - px * h, y1: my - py * h, x2: mx + px * h, y2: my + py * h, mx, my }
}

// ---- palette ---------------------------------------------------------------------

const PAL: Record<Terrain, RGB> = {
  [Terrain.DeepWater]: rgb('#173a5c'),
  [Terrain.Water]: rgb('#2e6f9f'),
  [Terrain.Sand]: rgb('#ddc687'),
  [Terrain.Grass]: rgb('#77aa58'),
  [Terrain.Fertile]: rgb('#559a47'),
  [Terrain.Forest]: rgb('#41763a'),
  [Terrain.Hills]: rgb('#99936e'),
  [Terrain.Mountain]: rgb('#7c7784'),
  [Terrain.Swamp]: rgb('#586a45'),
  [Terrain.Snow]: rgb('#e2e8ee'),
}

function isWater(t: Terrain | undefined): boolean {
  return t === Terrain.Water || t === Terrain.DeepWater
}

// ---- chunk bitmap cache -------------------------------------------------------------

export interface ChunkBitmap {
  key: string
  sig: string
  canvas: HTMLCanvasElement
  /** world-px origin + size of the canvas */
  ox: number
  oy: number
  w: number
  h: number
  /** water tile centers (world px) for the live shimmer layer */
  water: { x: number; y: number; deep: boolean; s: number }[]
  /** the discrete orientation (0..5) this bitmap was baked at — the renderer
   *  re-rotates it by (viewAngle − rot·60°) so a spin needs no re-bake. */
  rot: number
  lastUsed: number
  bytes: number
}

const cache = new Map<string, ChunkBitmap>()
let cacheBytes = 0
const MAX_BYTES = 300 * 1024 * 1024
const MAX_ENTRIES = 320

/** Snap zoom*dpr to a cache resolution bucket so we re-render rarely. */
export function bucketScale(zoomDpr: number): number {
  const buckets = [0.35, 0.5, 0.7, 1, 1.4, 2]
  for (const b of buckets) if (zoomDpr <= b + 0.001) return b
  return 2
}

export function chunkPixelBounds(cx: number, cy: number): { x: number; y: number; w: number; h: number } {
  const q0 = cx * CHUNK
  const r0 = cy * CHUNK
  const pad = S * 0.9
  // Unrotated bbox of the chunk's tile CENTERS (no lift headroom yet).
  const cMinX = S * SQRT3 * (q0 + r0 / 2) - (S * SQRT3) / 2
  const cMaxX = S * SQRT3 * (q0 + CHUNK - 1 + (r0 + CHUNK - 1) / 2) + (S * SQRT3) / 2
  const cMinY = S * 1.5 * r0 - S
  const cMaxY = S * 1.5 * (r0 + CHUNK - 1) + S
  // When the view is spun, tile positions rotate around the origin: take the AABB of
  // the four rotated corners so the bitmap still contains every tile. The bitmap bakes
  // at screen-aligned (rotated) positions and composites axis-aligned (no ctx.rotate),
  // so it isn't resampled by the spin.
  let minX = cMinX, maxX = cMaxX, minY = cMinY, maxY = cMaxY
  if (mAng) {
    minX = minY = Infinity; maxX = maxY = -Infinity
    for (const cx2 of [cMinX, cMaxX]) for (const cy2 of [cMinY, cMaxY]) {
      const x = cx2 * mCos - cy2 * mSin, y = cx2 * mSin + cy2 * mCos
      if (x < minX) minX = x; if (x > maxX) maxX = x
      if (y < minY) minY = y; if (y > maxY) maxY = y
    }
  }
  // Vertical headroom for the 2.5D lift: a tile can rise (tier +8) or sink (−8), and
  // cliff faces hang below — grow the canvas so neither clips. Lift stays screen-up
  // regardless of spin, so epad is always on Y. Harmless margin on flat worlds.
  const epad = tierLiftPx(8)
  return { x: minX - pad, y: minY - pad - epad, w: maxX - minX + 2 * pad, h: maxY - minY + 2 * pad + 2 * epad }
}

function neighborSig(st: GameState, cx: number, cy: number): string {
  let out = ''
  for (let dy = -1; dy <= 1; dy++)
    for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dy === 0) continue
      const c = st.chunks.get(chunkKey(cx + dx, cy + dy))
      out += c ? c.version : '-'
      out += ','
    }
  return out
}

/**
 * Get (rendering if needed, within budget.n) the cached bitmap for a chunk.
 * Returns a stale bitmap rather than null when out of budget.
 */
export function getChunkBitmap(
  st: GameState, ck: ClientChunk, scale: number, frame: number, budget: { n: number },
): ChunkBitmap | null {
  setBakeRot(st) // bake at the DISCRETE orientation (kept cached through the spin)
  const rotIdx = bakeRotIdx(st)
  const key = chunkKey(ck.cx, ck.cy)
  const sig = ck.version + '/' + scale + '/' + (st.lift25 ? 'L' : 'F') + '/R' + rotIdx + '/' + neighborSig(st, ck.cx, ck.cy)
  const old = cache.get(key)
  if (old && old.sig === sig) {
    old.lastUsed = frame
    return old
  }
  if (budget.n <= 0) {
    if (old) old.lastUsed = frame
    return old ?? null
  }
  budget.n--
  const entry = renderChunk(st, ck, scale, sig)
  entry.lastUsed = frame
  if (old) cacheBytes -= old.bytes
  cache.set(key, entry)
  cacheBytes += entry.bytes
  return entry
}

/** Evict least-recently-used chunk bitmaps beyond memory/entry caps. */
export function pruneChunkCache(frame: number): void {
  if (cacheBytes <= MAX_BYTES && cache.size <= MAX_ENTRIES) return
  const entries = [...cache.values()].sort((a, b) => a.lastUsed - b.lastUsed)
  for (const e of entries) {
    if (cacheBytes <= MAX_BYTES * 0.85 && cache.size <= MAX_ENTRIES * 0.85) break
    if (e.lastUsed >= frame) break // never drop what we just drew
    cache.delete(e.key)
    cacheBytes -= e.bytes
  }
}

// ---- chunk rendering ------------------------------------------------------------------

/** 2.5D terrain bake: draw tiles back-to-front, lifted by height, with filled
 *  cliff faces on every downhill edge so the relief reads as stepped ground. */
function renderChunkLifted(
  ctx: CanvasRenderingContext2D,
  st: GameState,
  ck: ClientChunk,
  centers: { x: number; y: number; ly: number; q: number; r: number; t: Terrain }[],
  water: ChunkBitmap['water'],
): void {
  // back-to-front: nearer (larger ground y) tiles paint last and occlude
  const order = centers.slice().sort((a, b) => a.y - b.y || a.x - b.x)
  for (const c of order) {
    const x = c.x
    const y = c.ly
    const tc = renderTier(st, c.q, c.r)
    // cliff faces on every downhill edge (fills the vertical gap to a lower nbr).
    // The face is a lit vertical wall: a warm rock/earth tint of the tile colour,
    // brightness set by which way it faces the sun, darkening toward its base for
    // a soft crease shadow — so stepped ground reads as solid blocks. Water draws
    // no faces (it's the flat sea); land still drops a cliff to the waterline.
    if (tc !== undefined && !isWater(c.t)) {
      const topDrop = tierLiftPx(tc)
      for (let d = 0; d < 6; d++) {
        const tn = renderTier(st, c.q + DIRS[d].q, c.r + DIRS[d].r)
        if (tn === undefined || tn >= tc) continue
        const botDrop = tierLiftPx(tn)
        if (topDrop - botDrop <= 0) continue
        const e = hexEdge(c.x, c.y, d, S, 0.5)
        // warm earthy rock, tinted by the tile so grass cliffs ≠ sand cliffs
        const rockTint = mixRgb(PAL[c.t], rgb('#6b5138'), 0.42)
        const lf = CLIFF_LIGHT[d]
        const g = ctx.createLinearGradient(0, e.y1 - topDrop, 0, e.y1 - botDrop)
        g.addColorStop(0, css(shade(rockTint, lf)))
        g.addColorStop(1, css(shade(rockTint, lf * 0.68)))
        ctx.fillStyle = g
        ctx.beginPath()
        ctx.moveTo(e.x1, e.y1 - topDrop)
        ctx.lineTo(e.x2, e.y2 - topDrop)
        ctx.lineTo(e.x2, e.y2 - botDrop)
        ctx.lineTo(e.x1, e.y1 - botDrop)
        ctx.closePath()
        ctx.fill()
        // sunlit top rim where the flat top meets the wall — ONLY on the light-facing
        // (front) edges. Away-facing back/side edges (lf <= 0.6) get no rim: a bright
        // line on a back edge is painted over the lower tile behind it and reads as a
        // white border cutting across the hex above. No constant floor, so it fades out.
        ctx.strokeStyle = css(rgb('#fff9e8'), 0.85 * Math.max(0, lf - 0.6))
        ctx.lineWidth = Math.max(1, S * 0.05)
        ctx.beginPath()
        ctx.moveTo(e.x1, e.y1 - topDrop)
        ctx.lineTo(e.x2, e.y2 - topDrop)
        ctx.stroke()
        // a true cliff (Δ>1) gets a thin contact shadow at its base
        if (tc - tn > 1) {
          ctx.strokeStyle = 'rgba(15,11,7,0.5)'
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(e.x1, e.y1 - botDrop)
          ctx.lineTo(e.x2, e.y2 - botDrop)
          ctx.stroke()
        }
      }
    }
    // Everything on the tile's TOP (fill, rim, foam, decorations) is clipped to this
    // hex MINUS any higher neighbour's raised footprint. Even-odd drops those overlaps,
    // so a lower tile never paints up into a taller hex beside or in front of it — the
    // taller hex blocks that portion off. The cliff walls above are drawn UNCLIPPED
    // because they must hang below the top into lower neighbours.
    ctx.save()
    ctx.beginPath()
    hexPathAt(ctx, x, y, S * 1.08)
    if (tc !== undefined) {
      for (let d = 0; d < 6; d++) {
        const nq = c.q + DIRS[d].q
        const nr = c.r + DIRS[d].r
        const tn = renderTier(st, nq, nr)
        if (tn === undefined || tn <= tc) continue
        const np = hexToPixel(nq, nr, S)
        hexPathAt(ctx, np.x, np.y - tierLiftPx(tn), S * 1.08)
      }
    }
    ctx.clip('evenodd')

    // top face fill (same rand sequence as the flat pass so tiles look identical)
    const rand = rngFor(c.q, c.r)
    let j = 1 + (rand() - 0.5) * 0.09
    if (tc !== undefined) j *= 1 + Math.max(-8, Math.min(8, tc)) * 0.05
    let base = shade(PAL[c.t], j)
    if (c.t === Terrain.Fertile) base = mixRgb(base, rgb('#3f8f3c'), rand() * 0.25)
    if (c.t === Terrain.Water) {
      let deepN = 0
      for (let d = 0; d < 6; d++) {
        const nt = terrainAt(st, c.q + DIRS[d].q, c.r + DIRS[d].r)
        if (nt === Terrain.DeepWater) deepN++
      }
      base = mixRgb(base, PAL[Terrain.DeepWater], Math.min(0.55, deepN * 0.14))
    }
    ctx.fillStyle = css(base)
    ctx.beginPath()
    hexPathAt(ctx, x, y, S * 1.08)
    ctx.fill()
    // subtle per-hex silhouette: a faint dark rim inset from the edge so adjacent
    // same-height tiles (esp. dense forest) stay legible without a hard grid look.
    if (!isWater(c.t)) {
      ctx.beginPath()
      hexPathAt(ctx, x, y, S * 0.99)
      ctx.strokeStyle = 'rgba(18,28,14,0.16)'
      ctx.lineWidth = Math.max(0.7, S * 0.03)
      ctx.stroke()
    }
    if (isWater(c.t)) water.push({ x, y, deep: c.t === Terrain.DeepWater, s: rand() })
    if (isWater(c.t)) {
      for (let d = 0; d < 6; d++) {
        const nt = terrainAt(st, c.q + DIRS[d].q, c.r + DIRS[d].r)
        if (nt === undefined || isWater(nt)) continue
        const e = hexEdge(x, y, d, S, 0.43)
        ctx.strokeStyle = 'rgba(228,242,246,0.34)'
        ctx.lineWidth = S * 0.24
        ctx.beginPath()
        ctx.moveTo(e.x1, e.y1)
        ctx.lineTo(e.x2, e.y2)
        ctx.stroke()
        ctx.strokeStyle = 'rgba(245,252,255,0.5)'
        ctx.lineWidth = S * 0.09
        const e2 = hexEdge(x, y, d, S, 0.47)
        ctx.beginPath()
        ctx.moveTo(e2.x1, e2.y1)
        ctx.lineTo(e2.x2, e2.y2)
        ctx.stroke()
      }
    }
    decorate(ctx, c.q, c.r, x, y, c.t, treeAt(st, c.q, c.r))
    ctx.restore()
  }
}

function renderChunk(st: GameState, ck: ClientChunk, scale: number, sig: string): ChunkBitmap {
  const b = chunkPixelBounds(ck.cx, ck.cy)
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.ceil(b.w * scale))
  canvas.height = Math.max(1, Math.ceil(b.h * scale))
  const ctx = canvas.getContext('2d')!
  ctx.scale(scale, scale)
  ctx.translate(-b.x, -b.y)
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  const water: ChunkBitmap['water'] = []
  const lift = !!ck.elev && st.hasElev && st.lift25
  const centers: { x: number; y: number; ly: number; q: number; r: number; t: Terrain }[] = []
  for (let i = 0; i < CHUNK * CHUNK; i++) {
    const { q, r } = tileFromIdx(ck.cx, ck.cy, i)
    const p = hexToPixel(q, r, S)
    const t = ck.tiles[i] as Terrain
    // Water sits flat at sea level (tier 0) so the ocean reads as one plane.
    const dy = lift ? (isWater(t) ? 0 : tierLiftPx(ck.elev![i] ?? 0)) : 0
    centers.push({ x: p.x, y: p.y, ly: p.y - dy, q, r, t })
  }

  if (lift) {
    renderChunkLifted(ctx, st, ck, centers, water)
    // roads / ground items / spills ride their tiles' lifted centres
    const centersL = centers.map((c) => ({ x: c.x, y: c.ly, q: c.q, r: c.r, t: c.t }))
    drawRoads(ctx, st, ck, centersL)
    drawDrops(ctx, ck, centersL)
    drawSpills(ctx, ck, centersL)
    return {
      key: chunkKey(ck.cx, ck.cy), sig, canvas,
      ox: b.x, oy: b.y, w: b.w, h: b.h,
      water, rot: bakeRotIdx(st), lastUsed: 0, bytes: canvas.width * canvas.height * 4,
    }
  }

  // pass 1: base fills (slightly oversized hexes tile seamlessly)
  for (const c of centers) {
    const rand = rngFor(c.q, c.r)
    let j = 1 + (rand() - 0.5) * 0.09
    // relief shading: higher ground reads lighter, lowlands darker (elevation feature)
    const tier = elevAt(st, c.q, c.r)
    if (tier !== undefined) j *= 1 + Math.max(-8, Math.min(8, tier)) * 0.05
    let base = shade(PAL[c.t], j)
    if (c.t === Terrain.Fertile) base = mixRgb(base, rgb('#3f8f3c'), rand() * 0.25)
    if (c.t === Terrain.Water) {
      // two-tone water: darken toward adjacent deep water
      let deepN = 0
      for (let d = 0; d < 6; d++) {
        const nt = terrainAt(st, c.q + DIRS[d].q, c.r + DIRS[d].r)
        if (nt === Terrain.DeepWater) deepN++
      }
      base = mixRgb(base, PAL[Terrain.DeepWater], Math.min(0.55, deepN * 0.14))
    }
    ctx.fillStyle = css(base)
    ctx.beginPath()
    hexPathAt(ctx, c.x, c.y, S * 1.08)
    ctx.fill()
    if (isWater(c.t)) water.push({ x: c.x, y: c.y, deep: c.t === Terrain.DeepWater, s: rand() })
  }

  // pass 2: coast foam on the water side of every water|land edge
  for (const c of centers) {
    if (!isWater(c.t)) continue
    for (let d = 0; d < 6; d++) {
      const nt = terrainAt(st, c.q + DIRS[d].q, c.r + DIRS[d].r)
      if (nt === undefined || isWater(nt)) continue
      const e = hexEdge(c.x, c.y, d, S, 0.43)
      ctx.strokeStyle = 'rgba(228,242,246,0.34)'
      ctx.lineWidth = S * 0.24
      ctx.beginPath()
      ctx.moveTo(e.x1, e.y1)
      ctx.lineTo(e.x2, e.y2)
      ctx.stroke()
      ctx.strokeStyle = 'rgba(245,252,255,0.5)'
      ctx.lineWidth = S * 0.09
      const e2 = hexEdge(c.x, c.y, d, S, 0.47)
      ctx.beginPath()
      ctx.moveTo(e2.x1, e2.y1)
      ctx.lineTo(e2.x2, e2.y2)
      ctx.stroke()
    }
  }

  // pass 3: decorations
  for (const c of centers) decorate(ctx, c.q, c.r, c.x, c.y, c.t, treeAt(st, c.q, c.r))

  // pass 3.5: cliff faces — a dark drop on the higher side of any edge where the
  // height tier falls by more than one. This is the Δ>1 "impassable" rule made
  // visible: the line marks exactly where units can't step across.
  if (ck.elev) {
    for (const c of centers) {
      const tc = elevAt(st, c.q, c.r)
      if (tc === undefined) continue
      for (let d = 0; d < 6; d++) {
        const tn = elevAt(st, c.q + DIRS[d].q, c.r + DIRS[d].r)
        if (tn === undefined || tc - tn <= 1) continue // draw once, from the top of the drop
        const e = hexEdge(c.x, c.y, d, S, 0.5)
        ctx.strokeStyle = 'rgba(38,28,20,0.5)'
        ctx.lineWidth = S * (0.16 + Math.min(0.32, (tc - tn) * 0.05))
        ctx.beginPath()
        ctx.moveTo(e.x1, e.y1)
        ctx.lineTo(e.x2, e.y2)
        ctx.stroke()
        ctx.strokeStyle = 'rgba(15,11,8,0.72)'
        ctx.lineWidth = S * 0.055
        ctx.beginPath()
        ctx.moveTo(e.x1, e.y1)
        ctx.lineTo(e.x2, e.y2)
        ctx.stroke()
      }
    }
  }

  // pass 4: roads baked into the cache
  drawRoads(ctx, st, ck, centers)

  // pass 5: ground items (dropped tools/weapons) baked into the cache
  drawDrops(ctx, ck, centers)

  // pass 6: spilled resource piles (a razed town hall's stores)
  drawSpills(ctx, ck, centers)

  return {
    key: chunkKey(ck.cx, ck.cy), sig, canvas,
    ox: b.x, oy: b.y, w: b.w, h: b.h,
    water, rot: bakeRotIdx(st), lastUsed: 0, bytes: canvas.width * canvas.height * 4,
  }
}

// ---- ground items -------------------------------------------------------------------

function drawDrops(
  ctx: CanvasRenderingContext2D,
  ck: ClientChunk,
  centers: { x: number; y: number; q: number; r: number }[],
): void {
  if (ck.drops.size === 0) return
  for (const [i, item] of ck.drops) {
    const c = centers[i]
    if (!c) continue
    // A soft ground shadow so the item reads on any terrain.
    ctx.fillStyle = 'rgba(0,0,0,0.32)'
    ctx.beginPath()
    ctx.ellipse(c.x, c.y + S * 0.16, S * 0.34, S * 0.15, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.save()
    ctx.translate(c.x, c.y)
    drawItemGlyph(ctx, item, S * 0.85)
    ctx.restore()
  }
}

// ---- resource spills ----------------------------------------------------------------

function drawSpills(
  ctx: CanvasRenderingContext2D,
  ck: ClientChunk,
  centers: { x: number; y: number; q: number; r: number }[],
): void {
  if (!ck.spills || ck.spills.size === 0) return
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `${Math.round(S * 0.6)}px sans-serif`
  for (const [i] of ck.spills) {
    const c = centers[i]
    if (!c) continue
    ctx.fillStyle = 'rgba(0,0,0,0.32)'
    ctx.beginPath()
    ctx.ellipse(c.x, c.y + S * 0.18, S * 0.36, S * 0.16, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillText('📦', c.x, c.y)
  }
}

// ---- roads --------------------------------------------------------------------------

function drawRoads(
  ctx: CanvasRenderingContext2D, st: GameState, ck: ClientChunk,
  centers: { x: number; y: number; q: number; r: number }[],
): void {
  if (ck.roads.size === 0) return
  const tiles: { x: number; y: number; stubs: number[] }[] = []
  for (const i of ck.roads) {
    const c = centers[i]
    const stubs: number[] = []
    for (let d = 0; d < 6; d++) if (roadAt(st, c.q + DIRS[d].q, c.r + DIRS[d].r)) stubs.push(d)
    tiles.push({ x: c.x, y: c.y, stubs })
  }
  const pass = (width: number, color: string) => {
    ctx.strokeStyle = color
    ctx.fillStyle = color
    ctx.lineWidth = width
    ctx.beginPath()
    for (const t of tiles) {
      if (t.stubs.length === 0) {
        ctx.moveTo(t.x + width / 2, t.y)
        ctx.arc(t.x, t.y, width / 2, 0, Math.PI * 2)
        continue
      }
      for (const d of t.stubs) {
        const e = hexEdge(t.x, t.y, d, S, 0.5)
        ctx.moveTo(t.x, t.y)
        // gentle bow so paths read organic, not gridded
        const bend = ((d % 2) - 0.5) * S * 0.16
        const v = { x: e.mx - t.x, y: e.my - t.y }
        ctx.quadraticCurveTo(t.x + v.x * 0.5 - v.y * 0.01 * bend, t.y + v.y * 0.5 + v.x * 0.01 * bend, e.mx, e.my)
      }
    }
    ctx.stroke()
    ctx.fill()
  }
  pass(S * 0.56, '#57452f') // dark packed edge
  pass(S * 0.4, '#a5865c') // warm dirt-stone surface
  // scattered paving stones
  ctx.fillStyle = 'rgba(120,96,64,0.8)'
  for (const t of tiles) {
    const rand = rngFor(t.x | 0, t.y | 0, 7)
    for (const d of t.stubs) {
      const e = hexEdge(t.x, t.y, d, S, 0.5)
      for (let k = 0; k < 3; k++) {
        const f = 0.15 + rand() * 0.7
        const ox = (rand() - 0.5) * S * 0.16
        const oy = (rand() - 0.5) * S * 0.16
        ctx.beginPath()
        ctx.arc(t.x + (e.mx - t.x) * f + ox, t.y + (e.my - t.y) * f + oy, S * 0.045 + rand() * S * 0.03, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }
}

// ---- per-terrain decorations -----------------------------------------------------------

function decorate(ctx: CanvasRenderingContext2D, q: number, r: number, x: number, y: number, t: Terrain, hasTree: boolean): void {
  const rand = rngFor(q, r)
  rand() // burn one so decoration layout differs from base jitter
  // Woodland is no longer a biome: a standing tree is a per-hex flag over ordinary
  // grass, so draw the trees wherever the flag is set (the base fill stays grass).
  if (hasTree) return decForest(ctx, rand, x, y)
  switch (t) {
    case Terrain.Grass: return decGrass(ctx, rand, x, y)
    case Terrain.Fertile: return decFertile(ctx, rand, x, y)
    case Terrain.Forest: return decForest(ctx, rand, x, y)
    case Terrain.Hills: return decHills(ctx, rand, x, y)
    case Terrain.Mountain: return decMountain(ctx, rand, x, y)
    case Terrain.Sand: return decSand(ctx, rand, x, y)
    case Terrain.Swamp: return decSwamp(ctx, rand, x, y)
    case Terrain.Snow: return decSnow(ctx, rand, x, y)
    case Terrain.Water:
    case Terrain.DeepWater: return decWater(ctx, rand, x, y, t === Terrain.DeepWater)
  }
}

function decGrass(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  const n = 8 + Math.floor(rand() * 6)
  for (let i = 0; i < n; i++) {
    const px = x + (rand() - 0.5) * S * 1.4
    const py = y + (rand() - 0.5) * S * 1.3
    const dark = rand() < 0.55
    ctx.strokeStyle = dark ? 'rgba(70,110,50,0.55)' : 'rgba(160,200,120,0.5)'
    ctx.lineWidth = 0.9
    ctx.beginPath()
    ctx.moveTo(px, py)
    ctx.lineTo(px + (rand() - 0.5) * 1.4, py - 1.6 - rand() * 1.4)
    ctx.stroke()
  }
  if (rand() < 0.16) { // rare wildflowers
    ctx.fillStyle = rand() < 0.5 ? '#e8e06a' : '#e0e6ef'
    for (let i = 0; i < 3; i++) {
      ctx.beginPath()
      ctx.arc(x + (rand() - 0.5) * S, y + (rand() - 0.5) * S, 0.8, 0, Math.PI * 2)
      ctx.fill()
    }
  }
}

function decFertile(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  ctx.save()
  ctx.beginPath()
  hexPathAt(ctx, x, y, S * 1.02)
  ctx.clip()
  // lush horizontal striping
  const phase = rand() * 4
  for (let i = -3; i <= 3; i++) {
    const yy = y + i * S * 0.3 + Math.sin(phase + i) * 1.4
    ctx.strokeStyle = i % 2 === 0 ? 'rgba(60,130,55,0.4)' : 'rgba(120,180,90,0.32)'
    ctx.lineWidth = S * 0.14
    ctx.beginPath()
    ctx.moveTo(x - S, yy)
    ctx.quadraticCurveTo(x, yy + (rand() - 0.5) * 2.5, x + S, yy)
    ctx.stroke()
  }
  ctx.restore()
  for (let i = 0; i < 5; i++) { // rich speckles
    ctx.fillStyle = 'rgba(190,220,130,0.5)'
    ctx.beginPath()
    ctx.arc(x + (rand() - 0.5) * S * 1.2, y + (rand() - 0.5) * S * 1.1, 0.8, 0, Math.PI * 2)
    ctx.fill()
  }
}

// A tree's 3-lobe canopy path at a given radius scale (used twice: a fatter dark
// pass for the silhouette rim, then the body on top).
function canopyPath(ctx: CanvasRenderingContext2D, px: number, py: number, rr: number, k: number): void {
  ctx.beginPath()
  ctx.arc(px, py, rr * k, 0, Math.PI * 2)
  ctx.arc(px - rr * 0.6, py + rr * 0.28, rr * 0.72 * k, 0, Math.PI * 2)
  ctx.arc(px + rr * 0.6, py + rr * 0.28, rr * 0.72 * k, 0, Math.PI * 2)
}

function decForest(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  const n = 4 + Math.floor(rand() * 3) // fewer, bolder trees
  const trees: { px: number; py: number; rr: number }[] = []
  for (let i = 0; i < n; i++) {
    trees.push({
      px: x + (rand() - 0.5) * S * 0.82, // contained to the hex so canopies don't bleed across tiles
      py: y + (rand() - 0.5) * S * 0.7,
      rr: S * (0.23 + rand() * 0.12),
    })
  }
  trees.sort((a, b) => a.py - b.py)
  for (const tr of trees) {
    // cast shadow grounds the tree on its tile
    ctx.fillStyle = 'rgba(22,42,20,0.26)'
    ctx.beginPath()
    ctx.ellipse(tr.px + tr.rr * 0.22, tr.py + tr.rr * 1.0, tr.rr * 1.0, tr.rr * 0.4, 0, 0, Math.PI * 2)
    ctx.fill()
    // trunk
    ctx.strokeStyle = '#4a3620'
    ctx.lineWidth = Math.max(1.3, tr.rr * 0.3)
    ctx.beginPath()
    ctx.moveTo(tr.px, tr.py + tr.rr * 0.45)
    ctx.lineTo(tr.px, tr.py + tr.rr * 1.05)
    ctx.stroke()
    // silhouette rim: a fatter near-black canopy behind the body
    ctx.fillStyle = '#173216'
    canopyPath(ctx, tr.px, tr.py, tr.rr, 1.16)
    ctx.fill()
    // canopy body
    ctx.fillStyle = '#367030'
    canopyPath(ctx, tr.px, tr.py, tr.rr, 1.0)
    ctx.fill()
    // sunlit top-left
    ctx.fillStyle = 'rgba(126,182,96,0.85)'
    ctx.beginPath()
    ctx.arc(tr.px - tr.rr * 0.34, tr.py - tr.rr * 0.4, tr.rr * 0.42, 0, Math.PI * 2)
    ctx.fill()
  }
}

function decHills(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  const n = 3 + Math.floor(rand() * 2)
  for (let i = 0; i < n; i++) {
    const px = x + (rand() - 0.5) * S * 0.9
    const py = y - S * 0.4 + i * S * 0.34 + (rand() - 0.5) * 3
    const w = S * (0.4 + rand() * 0.35)
    // ridge: shadow under, light above
    ctx.strokeStyle = 'rgba(90,85,58,0.65)'
    ctx.lineWidth = 1.6
    ctx.beginPath()
    ctx.moveTo(px - w, py)
    ctx.quadraticCurveTo(px, py - S * 0.28, px + w, py)
    ctx.stroke()
    ctx.strokeStyle = 'rgba(190,184,150,0.55)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(px - w * 0.8, py - 1)
    ctx.quadraticCurveTo(px, py - S * 0.28 - 1, px + w * 0.7, py - 1.2)
    ctx.stroke()
  }
  if (rand() < 0.4) {
    ctx.fillStyle = 'rgba(120,114,90,0.9)'
    ctx.beginPath()
    ctx.arc(x + (rand() - 0.5) * S, y + S * 0.4, 1.5 + rand(), 0, Math.PI * 2)
    ctx.fill()
  }
}

function decMountain(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  const peaks = 1 + Math.floor(rand() * 2)
  for (let i = 0; i < peaks + 1; i++) {
    const main = i === 0
    const px = x + (main ? (rand() - 0.5) * S * 0.3 : (rand() - 0.5) * S * 1.0)
    const py = y + (main ? S * 0.35 : S * 0.4 + (rand() - 0.5) * S * 0.3)
    const h = main ? S * (1.1 + rand() * 0.25) : S * (0.5 + rand() * 0.3)
    const w = h * (0.62 + rand() * 0.15)
    const tipX = px + (rand() - 0.5) * w * 0.25
    const tipY = py - h
    // shadow (right) face
    ctx.fillStyle = '#5b566a'
    ctx.beginPath()
    ctx.moveTo(tipX, tipY)
    ctx.lineTo(px + w, py)
    ctx.lineTo(px, py)
    ctx.closePath()
    ctx.fill()
    // lit (left) face
    ctx.fillStyle = '#98939f'
    ctx.beginPath()
    ctx.moveTo(tipX, tipY)
    ctx.lineTo(px, py)
    ctx.lineTo(px - w, py)
    ctx.closePath()
    ctx.fill()
    // facet crease
    ctx.strokeStyle = 'rgba(50,46,60,0.5)'
    ctx.lineWidth = 0.9
    ctx.beginPath()
    ctx.moveTo(tipX, tipY)
    ctx.lineTo(px, py)
    ctx.stroke()
    // snowcap with jagged bottom
    const sc = h * 0.34
    ctx.fillStyle = '#eef2f7'
    ctx.beginPath()
    ctx.moveTo(tipX, tipY)
    ctx.lineTo(tipX + sc * 0.62, tipY + sc)
    ctx.lineTo(tipX + sc * 0.3, tipY + sc * 0.75)
    ctx.lineTo(tipX, tipY + sc * 1.05)
    ctx.lineTo(tipX - sc * 0.3, tipY + sc * 0.7)
    ctx.lineTo(tipX - sc * 0.62, tipY + sc)
    ctx.closePath()
    ctx.fill()
    ctx.fillStyle = 'rgba(190,200,215,0.8)' // cap shadow side
    ctx.beginPath()
    ctx.moveTo(tipX, tipY)
    ctx.lineTo(tipX + sc * 0.62, tipY + sc)
    ctx.lineTo(tipX + sc * 0.3, tipY + sc * 0.75)
    ctx.lineTo(tipX, tipY + sc * 1.05)
    ctx.closePath()
    ctx.fill()
  }
  // scree dots at the base
  ctx.fillStyle = 'rgba(70,66,80,0.6)'
  for (let i = 0; i < 4; i++) {
    ctx.beginPath()
    ctx.arc(x + (rand() - 0.5) * S * 1.2, y + S * 0.55 + (rand() - 0.5) * 3, 0.8 + rand() * 0.8, 0, Math.PI * 2)
    ctx.fill()
  }
}

function decSand(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  const n = 2 + Math.floor(rand() * 2)
  for (let i = 0; i < n; i++) {
    const px = x + (rand() - 0.5) * S * 0.8
    const py = y + (rand() - 0.5) * S * 0.9
    const w = S * (0.35 + rand() * 0.4)
    ctx.strokeStyle = 'rgba(176,146,86,0.55)' // dune shadow
    ctx.lineWidth = 1.4
    ctx.beginPath()
    ctx.moveTo(px - w, py)
    ctx.quadraticCurveTo(px, py - S * 0.2, px + w, py + rand() * 2)
    ctx.stroke()
    ctx.strokeStyle = 'rgba(240,222,168,0.6)' // sunlit crest
    ctx.lineWidth = 0.9
    ctx.beginPath()
    ctx.moveTo(px - w * 0.9, py - 1.1)
    ctx.quadraticCurveTo(px, py - S * 0.2 - 1.1, px + w * 0.8, py - 1)
    ctx.stroke()
  }
  ctx.fillStyle = 'rgba(160,132,80,0.5)'
  for (let i = 0; i < 4; i++) {
    ctx.beginPath()
    ctx.arc(x + (rand() - 0.5) * S * 1.3, y + (rand() - 0.5) * S * 1.2, 0.7, 0, Math.PI * 2)
    ctx.fill()
  }
}

function decSwamp(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  const pools = 1 + Math.floor(rand() * 2)
  for (let i = 0; i < pools; i++) {
    const px = x + (rand() - 0.5) * S * 0.8
    const py = y + (rand() - 0.5) * S * 0.7
    const rx = S * (0.28 + rand() * 0.2)
    const ry = rx * (0.5 + rand() * 0.2)
    ctx.fillStyle = 'rgba(52,74,66,0.85)' // murky pool
    ctx.beginPath()
    ctx.ellipse(px, py, rx, ry, (rand() - 0.5) * 0.6, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = 'rgba(118,132,88,0.5)' // muddy rim
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.ellipse(px, py, rx + 0.8, ry + 0.8, (rand() - 0.5) * 0.6, 0, Math.PI * 2)
    ctx.stroke()
    ctx.fillStyle = 'rgba(150,168,120,0.4)' // scum glint
    ctx.beginPath()
    ctx.ellipse(px - rx * 0.3, py - ry * 0.3, rx * 0.3, ry * 0.25, 0, 0, Math.PI * 2)
    ctx.fill()
  }
  const reeds = 3 + Math.floor(rand() * 3)
  for (let i = 0; i < reeds; i++) {
    const px = x + (rand() - 0.5) * S * 1.2
    const py = y + (rand() - 0.5) * S * 1.1
    ctx.strokeStyle = '#6f7f45'
    ctx.lineWidth = 0.9
    ctx.beginPath()
    ctx.moveTo(px, py)
    ctx.quadraticCurveTo(px + (rand() - 0.5) * 2, py - 3, px + (rand() - 0.5) * 3, py - 5 - rand() * 2)
    ctx.stroke()
    ctx.fillStyle = '#8a7a4a' // cattail head
    ctx.fillRect(px - 0.6, py - 6 - rand() * 2, 1.2, 2.2)
  }
}

function decSnow(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number): void {
  for (let i = 0; i < 2; i++) { // soft blue shadow drifts
    ctx.fillStyle = 'rgba(168,190,214,0.35)'
    ctx.beginPath()
    ctx.ellipse(
      x + (rand() - 0.5) * S * 0.9, y + (rand() - 0.5) * S * 0.8,
      S * (0.25 + rand() * 0.25), S * (0.1 + rand() * 0.1), (rand() - 0.5), 0, Math.PI * 2,
    )
    ctx.fill()
  }
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  for (let i = 0; i < 3; i++) {
    ctx.beginPath()
    ctx.arc(x + (rand() - 0.5) * S * 1.2, y + (rand() - 0.5) * S * 1.1, 0.6, 0, Math.PI * 2)
    ctx.fill()
  }
}

function decWater(ctx: CanvasRenderingContext2D, rand: () => number, x: number, y: number, deep: boolean): void {
  const n = deep ? 1 : 2
  for (let i = 0; i < n; i++) {
    const px = x + (rand() - 0.5) * S * 0.9
    const py = y + (rand() - 0.5) * S * 0.9
    const w = S * (0.2 + rand() * 0.25)
    ctx.strokeStyle = deep ? 'rgba(10,32,54,0.5)' : 'rgba(20,60,95,0.45)'
    ctx.lineWidth = 1.1
    ctx.beginPath()
    ctx.moveTo(px - w, py)
    ctx.quadraticCurveTo(px - w * 0.4, py - 1.6, px, py)
    ctx.quadraticCurveTo(px + w * 0.4, py + 1.6, px + w, py)
    ctx.stroke()
  }
}

// ---- live water shimmer -----------------------------------------------------------------

/**
 * Animated sparkle over water, drawn each frame over the cached terrain.
 * ctx must already be in world transform. Cheap: skipped when zoomed far out.
 */
export function drawWaterShimmer(
  ctx: CanvasRenderingContext2D, bmp: ChunkBitmap, now: number, zoom: number,
  viewMinX: number, viewMinY: number, viewMaxX: number, viewMaxY: number, cap: { n: number },
): void {
  if (zoom < 0.4) return
  const t = now * 0.001
  for (const w of bmp.water) {
    if (cap.n <= 0) return
    if (w.x < viewMinX || w.x > viewMaxX || w.y < viewMinY || w.y > viewMaxY) continue
    if (w.s < 0.45) continue // only ~55% of tiles sparkle; keeps it lively but cheap
    cap.n--
    const ph = t * (0.9 + w.s) + w.s * 37
    const a = Math.max(0, Math.sin(ph)) ** 3
    if (a < 0.05) continue
    const ox = Math.sin(ph * 0.7 + w.s * 11) * S * 0.35
    const oy = Math.cos(ph * 0.5 + w.s * 5) * S * 0.3
    ctx.strokeStyle = `rgba(220,240,250,${(w.deep ? 0.12 : 0.2) * a})`
    ctx.lineWidth = 1.2
    ctx.beginPath()
    ctx.moveTo(w.x + ox - S * 0.16, w.y + oy)
    ctx.lineTo(w.x + ox + S * 0.16, w.y + oy)
    ctx.stroke()
  }
}
